import PropTypes from 'prop-types';
import React, { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useMixpanel } from 'react-mixpanel-browser';

import { ANALYTICS_SET_REPORTS, ANALYTICS_SORT } from '../../../store/reducers/analytics';
import { DATE_TYPE_TOTAL, REPORT_TYPES } from './consts';
import { getCountryNameFromRow } from './utils';
import { AnalyticsReportType, exportReport } from '../../../store/actions/analytics/export-reports';
import { salesByChannelReportColumns } from './reportsColumnNames';
import { removeHiddenColumns } from './reportsColumnNames/utils';
import { setDateColumnByDateType } from '../../pages/Analytics/utils';
import { useVisibleColumnsToggler } from '../useVisibleColumnsToggler';
import { getInitialReportState } from '../../../utils/analytics';
import { reportBusinessActionToMixpanel } from '../../../utils/reportBusinessActionToMixpanel';

import { EmptyChart } from '../../pages/Analytics/EmptyChart';
import { ExportReportPopupToggler } from '../../pages/Reports';
import { ReportPagination } from './ReportPagination';
import { SendReportNotificationDialog } from './SendReportNotificationDialog';
import { SalesByChannelRow } from './SalesByChannelRow';
import { TableHeader } from '../TableHeader';
import { TableWrapper } from '../TableWrapper';
import { AnalyticsSpinner } from '../../common/Spinner/AnalyticsSpinner';
import { EmptyPage } from '../../pages/Analytics/EmptyPage';

import styles from './ReportTable.module.scss';

export function SalesByChannelTable({ data }) {
  const dispatch = useDispatch();
  const mixpanel = useMixpanel();
  const { supplier } = useSelector((state) => state.role);
  const {
    currentCurrency,
    countries,
    retailerNamesByIds,
    ui: {
      loading: { [REPORT_TYPES.SALES]: isLoading },
    },
  } = useSelector((state) => state.analytics);
  const { visibleColumns, dateType, sort, canLoadData } = useSelector(
    (state) => state.analytics.reports[REPORT_TYPES.SALES],
  );
  const { column: sortedBy, direction: sortDirection } = sort;
  const availableColumns = removeHiddenColumns(salesByChannelReportColumns);
  const { columnsDisplay, widget: visibleColumnsToggler } = useVisibleColumnsToggler({
    allColumns: availableColumns,
    visibleColumns,
    setVisibleColumns: (visibleColumns) =>
      dispatch({
        type: ANALYTICS_SET_REPORTS,
        payload: { reportType: REPORT_TYPES.SALES, data: { visibleColumns } },
      }),
  });

  const colSpan = visibleColumns.length - (dateType === DATE_TYPE_TOTAL ? 1 : 0);

  const dataWithExtendedFields = useMemo(
    () =>
      (data || []).map((record) => ({
        ...record,
        retailer_name: retailerNamesByIds[record.retailer_id],
        current_gmv: record.gmv[currentCurrency],
        country_name: getCountryNameFromRow(countries, record.country_code),
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data, dateType, visibleColumns],
  );

  const onExport = ({ onlySelectedColumns }) => {
    reportBusinessActionToMixpanel(mixpanel, 'sales-by-channel-report-export', { supplierName: supplier.name });
    dispatch(
      exportReport({
        reportType: AnalyticsReportType.SALES_BY_CHANNEL,
        omitFilters: ['date_type'],
        onlySelectedColumns,
      }),
    );
  };

  return (
    <div className={styles.reportTableWrapper}>
      <SendReportNotificationDialog reportType="Sales by Channel" />
      <div className={styles.tableHeaderOptions}>
        {visibleColumnsToggler}
        <ExportReportPopupToggler
          title="Export Sales by Channel report"
          onExport={onExport}
          disabled={!dataWithExtendedFields.length}
        />
      </div>

      <TableWrapper hasExpandedRow={false} className={styles.reportTable}>
        <TableHeader
          key={dateType}
          columns={columnsDisplay}
          overrides={(columns, columnsData) => setDateColumnByDateType(columns, columnsData.date, dateType)}
          columnsData={availableColumns}
          sort={{
            sortedBy: sortedBy,
            direction: sortDirection,
            initialColumn: getInitialReportState([], REPORT_TYPES.SALES).sort.column,
            onSortBy: (column, direction) => {
              dispatch({
                type: ANALYTICS_SORT,
                payload: { reportType: REPORT_TYPES.SALES, column, direction },
              });
            },
          }}
        />

        <tbody>
          {isLoading || !canLoadData ? (
            <tr>
              <td colSpan={colSpan} style={{ height: 300 }}>
                <AnalyticsSpinner />
              </td>
            </tr>
          ) : dataWithExtendedFields ? (
            dataWithExtendedFields.length > 0 ? (
              dataWithExtendedFields.map((row) => (
                <SalesByChannelRow key={`${row.date}:${row.retailer_id}:${row.country_code}`} row={row} />
              ))
            ) : (
              <tr>
                <td colSpan={colSpan}>
                  <EmptyChart />
                </td>
              </tr>
            )
          ) : (
            <tr>
              <td colSpan={colSpan}>
                <EmptyPage />
              </td>
            </tr>
          )}
        </tbody>
      </TableWrapper>

      <ReportPagination reportType={REPORT_TYPES.SALES} />
    </div>
  );
}

SalesByChannelTable.propTypes = {
  data: PropTypes.arrayOf(PropTypes.object),
};
